/**
 * Positioning F legs (inbound or empty return) can become revenue when the
 * company allows it and the tail's schedule still fits.
 *
 * Home is the tail base when that ICAO is set, otherwise the company home,
 * otherwise the first company base. No stock field is assumed.
 */
import { ferryTimesLegal, icaoKey, type AirportHours } from "./curfew";
import { curfewClashMessage } from "./curfew";
import type { Mission, OpsConstraintEnforcement, OpsFerryRules } from "./types";
import { DEFAULT_FERRY_RULES } from "./opsDeskSettings";

export type FerryConvertKind = "home" | "backhaul" | "fill";

export type FerryConvertPlan =
  | { ok: true; kind: FerryConvertKind; factor: number; homeIcao: string; warning?: string }
  | { ok: false; error: string };

const TURN_MS = 20 * 60_000;

function icao(raw: string | null | undefined): string {
  return icaoKey(raw);
}

export function ferryHomeIcao(input: {
  homeIcao?: string | null;
  tailBaseIcao?: string | null;
  bases?: readonly string[] | null;
}): string {
  const tail = icao(input.tailBaseIcao);
  if (tail) return tail;
  const home = icao(input.homeIcao);
  if (home) return home;
  for (const row of input.bases ?? []) {
    const code = icao(row);
    if (code) return code;
  }
  return "";
}

export function classifyFerryConvert(
  mission: Pick<Mission, "destIcao" | "parentMissionId" | "purpose" | "scenario">,
  homeIcao: string,
): FerryConvertKind {
  const dest = icao(mission.destIcao);
  const home = icao(homeIcao);
  const goingHome = Boolean(home) && dest === home;
  const emptyReturn = Boolean(mission.parentMissionId) || /empty return/i.test(`${mission.purpose} ${mission.scenario}`);
  if (goingHome && emptyReturn) return "backhaul";
  if (goingHome) return "home";
  if (emptyReturn && /empty return/i.test(`${mission.purpose} ${mission.scenario}`)) return "backhaul";
  return "fill";
}

function clampFactor(raw: number | null | undefined): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0 || raw > 1) return null;
  return Math.round(raw * 1000) / 1000;
}

/** Unset (0) stays off the card discount — full fare, not a baked example rate. */
export function ferryFillFactor(rules: Pick<OpsFerryRules, "backhaulFactor" | "fillFactor"> | null | undefined, kind: FerryConvertKind): number {
  const src = rules ?? DEFAULT_FERRY_RULES;
  const raw = kind === "fill" ? src.fillFactor : src.backhaulFactor;
  return clampFactor(raw) ?? 1;
}

function blockOf(row: Pick<Mission, "departureIso" | "eteMin">): { start: number; end: number } | null {
  const start = Date.parse(row.departureIso);
  if (!Number.isFinite(start)) return null;
  const eteMin = Number.isFinite(row.eteMin) && row.eteMin > 0 ? row.eteMin : 60;
  return { start, end: start + eteMin * 60_000 };
}

export function ferryScheduleBlocked(
  ferry: Pick<Mission, "id" | "aircraftId" | "departureIso" | "eteMin">,
  missions: readonly Pick<Mission, "id" | "aircraftId" | "departureIso" | "eteMin" | "status">[],
): string | null {
  const mine = blockOf(ferry);
  if (!mine) return "Ferry has no usable STD.";
  for (const other of missions) {
    if (other.id === ferry.id) continue;
    if (other.aircraftId !== ferry.aircraftId) continue;
    if (other.status === "cancelled" || other.status === "completed") continue;
    const block = blockOf(other);
    if (!block) continue;
    const overlaps = mine.start < block.end && block.start < mine.end + TURN_MS;
    if (overlaps) return "The schedule does not allow converting this ferry — the tail is already booked across that block.";
  }
  return null;
}

function isOpenFerry(mission: Pick<Mission, "flightType" | "status">): boolean {
  return mission.flightType === "ferry" && (mission.status === "pending" || mission.status === "in-progress");
}

export function planFerryConvert(input: {
  mission: Mission;
  missions?: readonly Mission[];
  homeIcao?: string | null;
  tailBaseIcao?: string | null;
  bases?: readonly string[] | null;
  rules?: OpsFerryRules | null;
  curfewEnforcement?: OpsConstraintEnforcement | null;
  hoursFor?: (icao: string | null | undefined) => AirportHours;
}): FerryConvertPlan {
  const mission = input.mission;
  if (!isOpenFerry(mission)) {
    return { ok: false, error: "Only an open ferry can convert to revenue." };
  }
  const rules = input.rules ?? DEFAULT_FERRY_RULES;
  if (rules.allowConvert === false) {
    return { ok: false, error: "Company settings do not allow converting a ferry to revenue." };
  }
  const homeIcao = ferryHomeIcao(input);
  const kind = classifyFerryConvert(mission, homeIcao);
  const blocked = ferryScheduleBlocked(mission, input.missions ?? []);
  if (blocked) return { ok: false, error: blocked };
  const dep = new Date(mission.departureIso);
  let warning: string | undefined;
  if (Number.isFinite(dep.getTime())) {
    const check = ferryTimesLegal(mission.originIcao, mission.destIcao, dep, mission.eteMin || 60, input.hoursFor);
    if (!check.legal) {
      warning =
        curfewClashMessage(check, input.hoursFor) ||
        `CURFEW  ferry ${icao(mission.originIcao)}–${icao(mission.destIcao)} overlaps quiet hours.`;
      if (input.curfewEnforcement === "block") return { ok: false, error: warning };
    }
  }
  return {
    ok: true,
    kind,
    factor: ferryFillFactor(rules, kind),
    homeIcao,
    ...(warning ? { warning } : {}),
  };
}
